import { initializeApp } from 'firebase/app';
import { connectAuthEmulator, getAuth } from 'firebase/auth';

/**
 * Firebase Client SDK Configuration
 *
 * SECURITY NOTE:
 * ==============
 * Firebase Web App credentials (apiKey, projectId, etc.) are SAFE to expose publicly.
 * These are client-side credentials used by Firebase SDK in the browser.
 *
 * Security is enforced by:
 * - Firebase Security Rules (server-side)
 * - Firebase App Check (optional anti-abuse)
 * - Backend validation of ID tokens
 *
 * DO NOT confuse these with:
 * - Firebase Admin SDK credentials (service account JSON) - MUST be kept private
 * - Database secrets or API keys - MUST be kept private
 *
 * All VITE_* env vars are bundled into client JS (public by design).
 */

// Validate Firebase configuration
const apiKey = import.meta.env.VITE_FIREBASE_API_KEY;
const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;

if (!apiKey || apiKey === 'your-firebase-api-key') {
  console.error(
    '❌ Firebase is not configured!\n\n' +
      'For production: Set VITE_FIREBASE_* environment variables\n' +
      'For local dev: Use the dev login script instead:\n' +
      '  ./scripts/start.sh dev:login\n\n' +
      'See 22-auth-portal/.env.example for required Firebase variables',
  );
  throw new Error('Firebase credentials not configured. See console for details.');
}

const firebaseConfig = {
  apiKey,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);

// Connect to Firebase Auth Emulator for local development
// SECURITY: Double-gated by import.meta.env.DEV + env var, restricted to localhost-only URLs
if (import.meta.env.DEV && import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true') {
  const rawUrl = import.meta.env.VITE_FIREBASE_EMULATOR_AUTH_URL || 'http://localhost:9099';
  try {
    const parsed = new URL(rawUrl);
    if (parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1') {
      connectAuthEmulator(auth, rawUrl, { disableWarnings: true });
      console.info(`[Firebase] Connected to Auth Emulator at ${rawUrl}`);
    } else {
      console.warn(`[Firebase] Refusing to connect Auth Emulator to non-localhost URL: ${rawUrl}`);
    }
  } catch {
    console.warn(`[Firebase] Invalid VITE_FIREBASE_EMULATOR_AUTH_URL "${rawUrl}". Using default.`);
    connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
    console.info('[Firebase] Connected to Auth Emulator at http://localhost:9099');
  }
}

export default app;
